import { useState } from "react";
import { Navigate, useParams, useSearchParams } from "react-router-dom";
import { Btn } from "@/components/ui/Btn/Btn";
import { Icon } from "@/components/ui/Icon/Icon";
import { Input } from "@/components/ui/Input/Input";
import { AppShell } from "@/components/app/AppShell";
import { Field } from "@/components/auth/FormShell";
import { useAuth } from "@/lib/AuthContext";
import {
  useBrand,
  useConnectAccount,
  useCreateScanJob,
  useDisconnectAccount,
  useScanJob,
  useSocialAccounts,
  useWorkspace,
} from "@/lib/queries";
import type { SocialAccountSummary, WorkspaceRole } from "@/lib/queries";
import styles from "./SocialAccounts.module.css";

/**
 * Social accounts — PUBLISH-side platform connections (DESIGN_SPEC §4.11).
 *
 * These are the accounts the brand publishes OUT to (Instagram, Facebook,
 * TikTok). They are DISTINCT from the Trends trend-scan READ sources, which
 * only pull trending data and never post. The connection cards below are
 * LIVE: they read real connection state from the accounts API and wire
 * "Connect"/"Disconnect" to the OAuth start + disconnect endpoints. No
 * tokens are ever read or written client-side — only non-secret metadata
 * (handle, status, expiry) comes back.
 *
 * How it's built (see docs/FEATURE_CATALOG.md → publishers):
 *   1. packages/core: AccountRecord (platform, handle, status,
 *      token-expiry) + Keys.account(...) (WS#<ws>#B#<brand> scoped).
 *      Tokens NEVER go in the table or the client. Mutations gate on the
 *      existing manageBrands perm (owner/admin).
 *   2. infra/lambdas/api accounts routes: list + start-oauth +
 *      oauth-callback + disconnect. The OAuth callback stores the
 *      access/refresh token in AWS SSM SecureString under the
 *      tenant-scoped path /sm/<stage>/social/<ws>/<brand>/<platform>; the
 *      table holds only non-secret metadata (handle, status, expiry).
 *   3. infra/lambdas/publish: read the token from SSM at publish time.
 *   4. THIS FILE: useSocialAccounts lists connection state;
 *      useConnectAccount starts OAuth (navigates to authorizeUrl or shows
 *      the not_configured message inline); useDisconnectAccount removes a
 *      connection. The OAuth round-trip returns to this page with
 *      ?connected / ?accountError query params, surfaced as banners.
 *
 * SEPARATE & LIVE: the "Teach your brain from your public content" section
 * below the publish cards is real today — it reuses the /learn brand-scan
 * pipeline (read-for-learning, not publish-out). See LearnFromPublic below.
 */

interface PublishPlatformDef {
  key: string;
  label: string;
  /** How this platform authenticates for publishing (no creds hardcoded). */
  auth: string;
}

// The three publish platforms this surface connects (matches PLATFORMS in
// packages/core/src/types.ts). These identities are real config, not
// sample data — connection state / handles arrive once OAuth is wired.
const PUBLISH_PLATFORMS: PublishPlatformDef[] = [
  { key: "instagram", label: "Instagram", auth: "Meta OAuth (your own app)" },
  { key: "facebook", label: "Facebook", auth: "Meta OAuth (your own app)" },
  { key: "tiktok", label: "TikTok", auth: "TikTok OAuth (your own app)" },
];

const MAX_URLS = 6;

// Friendly copy for the OAuth-callback error codes the API can return on
// `?accountError=<code>`. Unknown codes fall back to the generic message.
const ACCOUNT_ERROR_TEXT: Record<string, string> = {
  denied: "Connection was cancelled.",
  invalid_state: "That connection link expired — try again.",
  exchange_failed: "Couldn’t complete the connection — try again.",
};
function accountErrorText(code: string, platformLabel?: string): string {
  if (code === "not_configured") {
    return `This deployment isn’t configured for ${platformLabel ?? "this platform"} yet.`;
  }
  return ACCOUNT_ERROR_TEXT[code] ?? "Couldn’t complete the connection — try again.";
}

function platformLabel(key: string): string {
  return PUBLISH_PLATFORMS.find((p) => p.key === key)?.label ?? key;
}

export function SocialAccounts() {
  const { ws, brand } = useParams<{ ws: string; brand: string }>();
  const auth = useAuth();
  const brandQuery = useBrand(ws, brand);
  const wsQuery = useWorkspace(ws);
  const accountsQuery = useSocialAccounts(ws, brand);
  const [searchParams, setSearchParams] = useSearchParams();

  if (auth.status === "anonymous") {
    return <Navigate to={`/signin?next=/w/${ws}/b/${brand}/accounts`} replace />;
  }

  const b = brandQuery.data?.brand;
  const role = wsQuery.data?.role;
  const accounts = accountsQuery.data?.accounts ?? [];

  const connectedParam = searchParams.get("connected");
  const accountErrorParam = searchParams.get("accountError");

  const dismissBanner = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("connected");
    next.delete("accountError");
    setSearchParams(next, { replace: true });
  };

  return (
    <AppShell currentWorkspaceId={ws}>
      <div className={styles.page}>
        <Btn
          href={`/w/${ws}/b/${brand}`}
          variant="ghost"
          size="sm"
          iconLeft={<Icon name="arrow-left" size={13} />}
        >
          Back to brand
        </Btn>

        <header className={styles.head}>
          <h1 className={styles.title}>
            <Icon name="send" size={20} /> Social accounts{b ? ` · ${b.name}` : ""}
          </h1>
        </header>

        {connectedParam && (
          <div className={styles.bannerOk} role="status">
            <Icon name="check-circle" size={16} color="var(--success)" />
            <span>{platformLabel(connectedParam)} connected.</span>
            <Btn
              variant="ghost"
              size="sm"
              onClick={dismissBanner}
              aria-label="Dismiss"
              iconLeft={<Icon name="x" size={14} />}
            />
          </div>
        )}

        {accountErrorParam && (
          <div className={styles.bannerErr} role="alert">
            <Icon name="alert" size={16} color="var(--danger)" />
            <span>{accountErrorText(accountErrorParam, platformLabel(searchParams.get("platform") ?? ""))}</span>
            <Btn
              variant="ghost"
              size="sm"
              onClick={dismissBanner}
              aria-label="Dismiss"
              iconLeft={<Icon name="x" size={14} />}
            />
          </div>
        )}

        <p className={styles.publishNote}>
          These are <strong>publish connections</strong> — the accounts this
          brand posts out to. They’re separate from the{" "}
          <strong>trend-scan read sources</strong> on <strong>Trends</strong>,
          which only pull trending data and never publish. No tokens are
          hardcoded; each workspace connects its own.
        </p>

        <ul className={styles.grid}>
          {PUBLISH_PLATFORMS.map((p) => (
            <PlatformCard
              key={p.key}
              platform={p}
              ws={ws}
              brand={brand}
              role={role}
              account={accounts.find((a) => a.platform === p.key)}
            />
          ))}
        </ul>

        <LearnFromPublic ws={ws} brand={brand} role={role} />
      </div>
    </AppShell>
  );
}

const STATUS_LABEL: Record<SocialAccountSummary["status"], string> = {
  connected: "Connected",
  expired: "Expired",
  revoked: "Revoked",
};

/**
 * One publish-platform connection card — LIVE. Reads real connection state
 * from useSocialAccounts (passed in as `account`); owners/admins can
 * Connect (start OAuth) or Disconnect. Non-managers see read-only state.
 */
function PlatformCard({
  platform,
  ws,
  brand,
  role,
  account,
}: {
  platform: PublishPlatformDef;
  ws: string | undefined;
  brand: string | undefined;
  role: WorkspaceRole | undefined;
  account: SocialAccountSummary | undefined;
}) {
  const connect = useConnectAccount(ws, brand);
  const disconnect = useDisconnectAccount(ws, brand);
  // not_configured is an expected state, surfaced inline — not an error toast.
  const [notConfiguredMsg, setNotConfiguredMsg] = useState<string | undefined>();

  const canManage = role === "owner" || role === "admin";
  const connected = !!account;

  const expiresText = account?.expiresAt
    ? `Token expires ${new Date(account.expiresAt).toLocaleDateString()}`
    : "—";

  async function onConnect() {
    setNotConfiguredMsg(undefined);
    const res = await connect.mutateAsync({ platform: platform.key });
    if ("authorizeUrl" in res) {
      window.location.href = res.authorizeUrl;
    } else if (res.error === "not_configured") {
      setNotConfiguredMsg(res.message);
    }
  }

  async function onDisconnect() {
    await disconnect.mutateAsync({ platform: platform.key });
  }

  return (
    <li className={styles.card}>
      <div className={styles.cardTop}>
        <span
          className={`${styles.platformDot} ${styles[`dot_${platform.key}`] ?? ""}`}
          aria-hidden="true"
        />
        <span className={styles.platformName}>{platform.label}</span>
        <span className={connected ? styles.connected : styles.notConnected}>
          {connected ? STATUS_LABEL[account.status] : "Not connected"}
        </span>
      </div>
      <p className={styles.platformAuth}>{platform.auth}</p>

      {/* Metadata only — the token itself lives in SSM SecureString,
          never the table or this client. */}
      <dl className={styles.cardMeta}>
        <div><dt>Handle</dt><dd>{connected ? account.handle : "—"}</dd></div>
        <div><dt>Token expires</dt><dd>{connected ? expiresText : "—"}</dd></div>
      </dl>

      {canManage ? (
        connected ? (
          <>
            {disconnect.error && (
              <p className={styles.cardError}>{disconnect.error.message}</p>
            )}
            <Btn
              variant="secondary"
              size="sm"
              full
              onClick={onDisconnect}
              loading={disconnect.isPending}
            >
              Disconnect
            </Btn>
          </>
        ) : (
          <>
            {notConfiguredMsg && (
              <p className={styles.cardNote}>{notConfiguredMsg}</p>
            )}
            {connect.error && (
              <p className={styles.cardError}>{connect.error.message}</p>
            )}
            <Btn
              variant="secondary"
              size="sm"
              full
              onClick={onConnect}
              loading={connect.isPending}
            >
              Connect
            </Btn>
          </>
        )
      ) : null}
    </li>
  );
}

/**
 * "Teach your brain from your public content" — LIVE today. Distinct from
 * the deferred publish-OUT OAuth above: this is READ-for-learning. The
 * brand pastes PUBLIC URLs (website / blog / public pages); we reuse the
 * existing /learn brand-scan pipeline (useCreateScanJob → useScanJob) to
 * crawl what's publicly readable and fill the brain's voice / audience /
 * tone. The scan endpoint requires manageBrands (owner/admin) server-side,
 * so the form only renders for those roles.
 */
function LearnFromPublic({
  ws,
  brand,
  role,
}: {
  ws: string | undefined;
  brand: string | undefined;
  role: WorkspaceRole | undefined;
}) {
  const createScan = useCreateScanJob(ws, brand);
  const [jobId, setJobId] = useState<string | undefined>();
  const scanQuery = useScanJob(ws, brand, jobId);

  const [urls, setUrls] = useState<string[]>([""]);

  const job = scanQuery.data?.job;
  const running = job?.status === "queued" || job?.status === "running";

  // "Nothing readable" success — the scan completed but found nothing to
  // learn from. Surfaced honestly rather than as a fake win.
  const foundNothing =
    job?.status === "done" && /nothing readable|nothing usable|no.*readable/i.test(job.summary ?? "");

  const canManage = role === "owner" || role === "admin";

  const trimmedUrls = urls.map((u) => u.trim()).filter(Boolean);
  const canSubmit = !running && !createScan.isPending && trimmedUrls.length > 0;

  const setUrlAt = (i: number, v: string) =>
    setUrls((prev) => prev.map((u, idx) => (idx === i ? v : u)));
  const addUrl = () => setUrls((prev) => (prev.length >= MAX_URLS ? prev : [...prev, ""]));
  const removeUrl = (i: number) =>
    setUrls((prev) => (prev.length <= 1 ? prev : prev.filter((_, idx) => idx !== i)));

  async function onScan() {
    if (!canSubmit) return;
    const res = await createScan.mutateAsync({ urls: trimmedUrls });
    setJobId(res.jobId);
  }

  // Reset to a fresh form so the user can run another scan.
  const resetForAnother = () => {
    setJobId(undefined);
    createScan.reset();
  };

  return (
    <section className={styles.learn} aria-labelledby="learn-heading">
      <div className={styles.learnHead}>
        <h2 id="learn-heading" className={styles.learnTitle}>
          <Icon name="link" size={18} /> Teach your brain from your public content
        </h2>
        <p className={styles.learnSub}>
          Paste your <strong>public</strong> URLs — your website, blog, or any
          page that serves public HTML. We read what’s publicly available and
          update your brand brain’s voice, audience, and tone. This is{" "}
          <strong>reading for learning</strong>, not the publish connections
          above — we never post anything.
        </p>
      </div>

      <div className={styles.coverage} role="note">
        <Icon name="alert" size={15} />
        <div>
          <strong>What this can and can’t read.</strong> Instagram and TikTok
          usually require a login to view posts, so public scraping captures
          little from them without their official API — don’t expect IG/TikTok
          post content to come through here. This works best for public
          websites, blogs, and pages that serve public HTML. If a page returns
          nothing readable, we’ll tell you plainly rather than pretend it
          worked.
        </div>
      </div>

      {!canManage ? (
        <p className={styles.learnMuted}>
          Brand managers (workspace owners and admins) can run a public-content
          scan to update the brand brain.
        </p>
      ) : (
        <>
          {!running && job?.status !== "done" && (
            <div className={styles.learnForm}>
              <Field
                label="Public URLs"
                hint="Your homepage, blog, about / story pages, or a public profile page. One per row. Add up to 6."
              >
                <div className={styles.urlList}>
                  {urls.map((url, i) => (
                    <div key={i} className={styles.urlRow}>
                      <Input
                        value={url}
                        onChange={(e) => setUrlAt(i, e.target.value)}
                        placeholder="https://yourbrand.com"
                        inputMode="url"
                        icon="link"
                      />
                      {urls.length > 1 && (
                        <Btn
                          variant="ghost"
                          size="sm"
                          onClick={() => removeUrl(i)}
                          aria-label="Remove this URL"
                          iconLeft={<Icon name="x" size={14} />}
                        />
                      )}
                    </div>
                  ))}
                  {urls.length < MAX_URLS && (
                    <Btn
                      variant="ghost"
                      size="sm"
                      onClick={addUrl}
                      iconLeft={<Icon name="arrow-up-right" size={13} />}
                    >
                      Add another URL
                    </Btn>
                  )}
                </div>
              </Field>

              {createScan.error && (
                <p className={styles.learnError}>{createScan.error.message}</p>
              )}

              <Btn
                variant="primary"
                size="lg"
                full
                onClick={onScan}
                disabled={!canSubmit}
                loading={createScan.isPending}
                iconRight={<Icon name="zap" size={14} />}
              >
                Scan &amp; learn
              </Btn>
            </div>
          )}

          {running && (
            <div className={styles.learnStatus}>
              <span className={styles.spinner} aria-hidden="true" />
              <span>
                Reading your pages…{" "}
                {job?.status === "queued" ? "(queued)" : "(reading)"} This can
                take up to a minute.
              </span>
            </div>
          )}

          {jobId && scanQuery.isError && (
            <div className={styles.learnFailed}>
              <Icon name="alert" size={16} color="var(--danger)" />
              <div>
                <strong>Lost track of that scan.</strong>
                <p>
                  {scanQuery.error?.message ?? "Couldn’t load the scan status."}{" "}
                  Try again.
                </p>
                <Btn variant="secondary" size="sm" onClick={resetForAnother}>
                  Start over
                </Btn>
              </div>
            </div>
          )}

          {job?.status === "failed" && (
            <div className={styles.learnFailed}>
              <Icon name="alert" size={16} color="var(--danger)" />
              <div>
                <strong>Couldn’t learn from those pages.</strong>
                <p>
                  {job.error ??
                    job.summary ??
                    "Nothing usable was found. Your brain was left unchanged."}
                </p>
                <Btn variant="secondary" size="sm" onClick={resetForAnother}>
                  Try different URLs
                </Btn>
              </div>
            </div>
          )}

          {job?.status === "done" && foundNothing && (
            <div className={styles.learnFailed}>
              <Icon name="alert" size={16} color="var(--danger)" />
              <div>
                <strong>Nothing readable at those pages.</strong>
                <p>
                  {job.summary}. That usually means the pages need a login (like
                  most Instagram / TikTok posts) or didn’t return public HTML.
                  Your brain was left unchanged — try a public website or blog.
                </p>
                <Btn variant="secondary" size="sm" onClick={resetForAnother}>
                  Try different URLs
                </Btn>
              </div>
            </div>
          )}

          {job?.status === "done" && !foundNothing && (
            <div className={styles.learnDone}>
              <Icon name="check-circle" size={18} color="var(--success)" />
              <div>
                <strong>Your brand brain was updated.</strong>
                <p>{job.summary ?? "We read your pages and learned your voice."}</p>
                <Btn
                  variant="secondary"
                  size="sm"
                  onClick={resetForAnother}
                  iconLeft={<Icon name="refresh" size={13} />}
                >
                  Scan more pages
                </Btn>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}
